import { useState } from 'react';

import { describeApiError } from '../../lib/api-client';
import { useApiClient, useAuth } from '../../providers/auth-provider';
import { useTenants } from '../../hooks/use-tenants';

import { OrgTreePicker } from './OrgTreePicker';
import { inputStyle, mono, primaryButton, secondaryButton } from './ui';

import type { ProxyVaultConnection } from '../../lib/api-client';

/**
 * The starting document.
 *
 * A blank editor is the wrong first experience for a format nobody has written
 * before, and a form-based builder is the wrong second one — the spec is the
 * artifact, and hiding it behind widgets means an operator cannot diff it, keep
 * it in git, or move it between environments. So: real YAML, pre-filled with
 * the shape that covers most vendors, with the two decisions that matter
 * (which host, which variable) sitting where they are impossible to miss.
 */
const STARTER = `apiVersion: proxy-vault/v1
kind: Connection
metadata:
  name: vendor-production
spec:
  visibility: private            # private | children — may the subtree use this?
  materialization: aliased       # aliased keeps the value in custody

  secrets:
    - name: api_key
      env: VENDOR_API_KEY        # what your skill reads. You choose it.

  routes:
    - match: { host: api.vendor.com, path: "/**" }
      request:
        - { reveal: api_key, in: header, name: Authorization }
`;

export function AddConnectionModal({
  onCancel,
  onCreated,
}: {
  onCancel: () => void;
  onCreated: (connection: ProxyVaultConnection) => void;
}) {
  const api = useApiClient();
  const { user } = useAuth();
  const { tenants } = useTenants();
  const [spec, setSpec] = useState(STARTER);
  const [tenantId, setTenantId] = useState<string>(user?.tenant_id ?? '');
  const [asConnector, setAsConnector] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const created = await api.createProxyVaultConnection(spec, {
        ...(tenantId && tenantId !== user?.tenant_id && { tenantId }),
        asConnector,
      });
      onCreated(created);
    } catch (err) {
      setError(describeApiError(err, 'Could not create the connection'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.35)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50,
      }}
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="pv-add-modal"
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border-strong)',
          borderRadius: 10,
          width: 'min(760px, 92vw)',
          maxHeight: '88vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>Add connection</div>
          <div style={{ fontSize: 11, color: 'var(--text-soft)', marginTop: 2 }}>
            The document carries names, routes and operations — never a value. Bind values on the
            next page.
          </div>
        </div>

        <div style={{ padding: '10px 18px', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ fontSize: 11, color: 'var(--text-soft)' }}>Organization</label>
          <OrgTreePicker
            value={tenantId}
            onChange={setTenantId}
            tenants={tenants}
            hint={(t) => (t.id === user?.tenant_id ? 'this org' : null)}
            ariaLabel="Organization"
            testId="pv-add-org"
          />
          <label
            style={{ fontSize: 11, color: 'var(--text-soft)', display: 'flex', gap: 6, alignItems: 'center' }}
          >
            <input
              type="checkbox"
              checked={asConnector}
              onChange={(e) => setAsConnector(e.target.checked)}
            />
            Publish as a Connector (a template others instantiate)
          </label>
        </div>

        <textarea
          value={spec}
          onChange={(e) => setSpec(e.target.value)}
          spellCheck={false}
          data-testid="pv-add-spec"
          style={{
            ...inputStyle,
            margin: '0 18px',
            flex: 1,
            minHeight: 320,
            fontFamily: mono,
            fontSize: 12,
            lineHeight: 1.5,
            resize: 'vertical',
          }}
        />

        {error && (
          <div
            data-testid="pv-add-error"
            style={{
              margin: '10px 18px 0',
              padding: 10,
              borderRadius: 6,
              background: 'rgba(192,57,43,0.08)',
              color: '#c0392b',
              fontSize: 11,
              whiteSpace: 'pre-wrap',
              fontFamily: mono,
              maxHeight: 160,
              overflow: 'auto',
            }}
          >
            {error}
          </div>
        )}

        <div
          style={{
            padding: '12px 18px',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            borderTop: '1px solid var(--border)',
            marginTop: 12,
          }}
        >
          <button type="button" onClick={onCancel} style={secondaryButton}>
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={busy}
            style={{ ...primaryButton, opacity: busy ? 0.6 : 1 }}
            data-testid="pv-add-submit"
          >
            {busy ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}
